import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  WifiIcon,
  BuildingOfficeIcon,
  ServerStackIcon,
  XCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  TagIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle, Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import type { Gateway, Customer, Asset } from '../context/ManagementContext';
import managementService from '../api/managementService';
import AdvancedEntitySelector from './AdvancedEntitySelector';
import DataTable from './DataTable';
import { useDebounce } from '../hooks/useDebounce';

const gatewaySchema = z.object({
  id: z.string().min(1, 'Gateway ID is required'),
  name: z.string().optional(),
  hardwareUuid: z.string().min(1, 'Hardware UUID is required'),
  fetchIntervalMs: z.number().min(1000, 'Fetch interval must be at least 1000ms').optional(),
  energyFetchIntervalMs: z.number().min(1000, 'Energy fetch interval must be at least 1000ms').optional(),
  waterFetchIntervalMs: z.number().min(1000, 'Water fetch interval must be at least 1000ms').optional(),
  isPaused: z.boolean().optional(),
  customerId: z.string().optional(),
});

const resetSyncSchema = z.object({
  lastEnergyFetchTimestamp: z.string().optional(),
  lastWaterFetchTimestamp: z.string().optional(),
}).refine(data => data.lastEnergyFetchTimestamp || data.lastWaterFetchTimestamp, {
  message: "At least one timestamp must be provided"
});

type GatewayFormData = z.infer<typeof gatewaySchema>;
type ResetSyncFormData = z.infer<typeof resetSyncSchema>;

// Helper function to calculate fetch progress percentage
const calculateFetchProgress = (timestamp: string | null): number => {
  if (!timestamp) return 0;

  // Start of 2025 (0%)
  const startOf2025 = new Date('2025-01-01T00:00:00.000Z').getTime();
  // Current time (100%)
  const now = Date.now();
  // Timestamp to calculate
  const fetchTime = new Date(timestamp).getTime();

  // Calculate percentage
  const totalRange = now - startOf2025;
  const progressRange = fetchTime - startOf2025;

  // Ensure percentage is between 0 and 100
  return Math.max(0, Math.min(100, (progressRange / totalRange) * 100));
};

export default function GatewayManagement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isResetSyncOpen, setIsResetSyncOpen] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<Gateway | null>(null);

  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [selectedCustomerForFilter, setSelectedCustomerForFilter] = useState<Customer | null>(null);
  const [selectedAssetForFilter, setSelectedAssetForFilter] = useState<Asset | null>(null);
  const [connectionFilter, setConnectionFilter] = useState(searchParams.get('connection') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'name');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'asc');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isCustomerSelectorOpen, setIsCustomerSelectorOpen] = useState(false);
  const [isAssetSelectorOpen, setIsAssetSelectorOpen] = useState(false);
  const [isCustomerFilterSelectorOpen, setIsCustomerFilterSelectorOpen] = useState(false);
  const [isAssetFilterSelectorOpen, setIsAssetFilterSelectorOpen] = useState(false);
  const [selectedCustomerForForm, setSelectedCustomerForForm] = useState<Customer | null>(null);
  const [selectedAssetForForm, setSelectedAssetForForm] = useState<Asset | null>(null);
  const [isPopulateConfirmOpen, setIsPopulateConfirmOpen] = useState(false);
  const [gatewayToPopulate, setGatewayToPopulate] = useState<Gateway | null>(null);
  const [isForcePopulateGateway, setIsForcePopulateGateway] = useState(false);
  const queryClient = useQueryClient();

  // Function to update URL with current filter state
  const updateUrlParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (connectionFilter !== 'all') params.set('connection', connectionFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (sortBy !== 'name') params.set('sortBy', sortBy);
    if (sortOrder !== 'asc') params.set('sortOrder', sortOrder);
    if (selectedCustomerForFilter) params.set('customerId', selectedCustomerForFilter.id);
    if (selectedAssetForFilter) params.set('assetId', selectedAssetForFilter.id);
    setSearchParams(params, { replace: true });
  }, [searchTerm, currentPage, connectionFilter, statusFilter, sortBy, sortOrder, selectedCustomerForFilter, selectedAssetForFilter, setSearchParams]);

  const { data: gatewaysData, isLoading, error } = useQuery({
    queryKey: ['gateways', currentPage, debouncedSearchTerm, selectedCustomerForFilter?.id, selectedAssetForFilter?.id, connectionFilter, statusFilter, sortBy, sortOrder],
    queryFn: async () => {
      // For 'syncing' status, we need to fetch all gateways and filter on frontend
      const isFilteringSyncing = statusFilter === 'syncing';

      const queryParams = {
        page: isFilteringSyncing ? undefined : currentPage,
        limit: isFilteringSyncing ? undefined : 10,
        search: debouncedSearchTerm || undefined,
        customerId: selectedCustomerForFilter?.id || undefined,
        assetId: selectedAssetForFilter?.id || undefined,
        isOnline: connectionFilter === 'all' ? undefined : connectionFilter === 'online',
        status: isFilteringSyncing ? 'all' : statusFilter as 'running' | 'paused' | 'registered' | 'unregistered' | 'all',
        includeDeleted: false,
        sortBy: sortBy as 'name' | 'createdAt' | 'updatedAt',
        sortOrder: sortOrder as 'asc' | 'desc'
      };

      const result = await managementService.gateway.getAll(queryParams);

      // If filtering by syncing, filter the results on frontend
      if (isFilteringSyncing) {
        const incompleteSyncGateways = result.data.filter((gateway) => {
          const energyProgress = calculateFetchProgress(gateway.lastEnergyFetchTimestamp ?? null);
          const waterProgress = calculateFetchProgress(gateway.lastWaterFetchTimestamp ?? null);
          const overallProgress = Math.max(energyProgress, waterProgress);
          return overallProgress < 99.5; // Not 100% synced
        });

        // Apply pagination on filtered results
        const startIndex = (currentPage - 1) * 10;
        const endIndex = startIndex + 10;
        const paginatedData = incompleteSyncGateways.slice(startIndex, endIndex);

        return {
          data: paginatedData,
          pagination: {
            page: currentPage,
            limit: 10,
            total: incompleteSyncGateways.length,
            pages: Math.ceil(incompleteSyncGateways.length / 10)
          }
        };
      }

      return result;
    },
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => managementService.customer.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  const { data: assets = [] } = useQuery({
    queryKey: ['assets-all'],
    queryFn: () => managementService.asset.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  // Initialize entities from URL parameters on component mount
  useEffect(() => {
    const initializeFromUrl = async () => {
      const customerId = searchParams.get('customerId');
      const assetId = searchParams.get('assetId');

      // Handle customer loading
      if (customerId) {
        if (customers.length > 0) {
          const customer = customers.find(c => c.id === customerId);
          if (customer) {
            setSelectedCustomerForFilter(customer);
          } else {
            // If not found in loaded data, fetch it
            try {
              const fetchedCustomer = await managementService.customer.getById(customerId);
              setSelectedCustomerForFilter(fetchedCustomer);
            } catch (error) {
              console.warn('Failed to load customer from URL:', error);
            }
          }
        } else {
          // Data not loaded yet, try fetching directly
          try {
            const fetchedCustomer = await managementService.customer.getById(customerId);
            setSelectedCustomerForFilter(fetchedCustomer);
          } catch (error) {
            console.warn('Failed to load customer from URL:', error);
          }
        }
      }

      // Handle asset loading
      if (assetId) {
        if (assets.length > 0) {
          const asset = assets.find(a => a.id === assetId);
          if (asset) {
            setSelectedAssetForFilter(asset);
          } else {
            // If not found in loaded data, fetch it
            try {
              const fetchedAsset = await managementService.asset.getById(assetId);
              setSelectedAssetForFilter(fetchedAsset);
            } catch (error) {
              console.warn('Failed to load asset from URL:', error);
            }
          }
        } else {
          // Data not loaded yet, try fetching directly
          try {
            const fetchedAsset = await managementService.asset.getById(assetId);
            setSelectedAssetForFilter(fetchedAsset);
          } catch (error) {
            console.warn('Failed to load asset from URL:', error);
          }
        }
      }
    };

    initializeFromUrl();
  }, [searchParams]);


  // Update URL parameters whenever filter state changes
  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  const createMutation = useMutation({
    mutationFn: (data: GatewayFormData) => managementService.gateway.create({
      ...data,
      isOnline: false,
      isRegistered: false,
      isPaused: data.isPaused || false,
      isDeleted: false,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
      setIsCreateOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: GatewayFormData }) =>
      managementService.gateway.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] });
      setIsEditOpen(false);
      setSelectedGateway(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => managementService.gateway.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
  });

  const registerMutation = useMutation({
    mutationFn: (id: string) => managementService.gateway.update(id, { isRegistered: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
  });

  const unregisterMutation = useMutation({
    mutationFn: (id: string) => managementService.gateway.update(id, { isRegistered: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
  });

  const populateGatewayNamesMutation = useMutation({
    mutationFn: ({ gatewayId, onlyMissingNames }: { gatewayId: string; onlyMissingNames: boolean }) =>
      managementService.device.populateGatewayNames(gatewayId, onlyMissingNames),
    onSuccess: (result) => {
      // Optionally refresh device data if needed
      // queryClient.invalidateQueries({ queryKey: ['devices'] });

      // Show success message
      alert(`Gateway population completed: ${result.data.successCount} succeeded, ${result.data.failureCount} failed, ${result.data.skippedCount} skipped`);

      setIsPopulateConfirmOpen(false);
      setGatewayToPopulate(null);
    },
    onError: (error: any) => {
      console.error('Gateway name population failed:', error);
      alert('Gateway name population failed. Please try again.');
      setIsPopulateConfirmOpen(false);
      setGatewayToPopulate(null);
    },
  });

  const resetSyncMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ResetSyncFormData }) => {
      // Convert datetime-local format (YYYY-MM-DDTHH:MM) to full ISO string
      const convertToISO = (datetimeLocal?: string) => {
        if (!datetimeLocal) return undefined;
        return new Date(datetimeLocal).toISOString();
      };

      return managementService.gateway.resetSyncTimestamps(id, {
        lastEnergyFetchTimestamp: convertToISO(data.lastEnergyFetchTimestamp),
        lastWaterFetchTimestamp: convertToISO(data.lastWaterFetchTimestamp)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] });
      alert('Gateway sync timestamps reset successfully. The MQTT service will now re-fetch data from the specified dates.');
      setIsResetSyncOpen(false);
      setSelectedGateway(null);
      resetSync();
    },
    onError: (error: any) => {
      console.error('Reset sync timestamps failed:', error);
      alert('Failed to reset sync timestamps. Please try again.');
    },
  });

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<GatewayFormData>({
    resolver: zodResolver(gatewaySchema),
  });

  const {
    register: registerSync,
    handleSubmit: handleSubmitSync,
    formState: { errors: syncErrors },
    reset: resetSync
  } = useForm<ResetSyncFormData>({
    resolver: zodResolver(resetSyncSchema),
  });

  const resetForm = () => {
    reset();
    setSelectedGateway(null);
    setSelectedCustomerForForm(null);
    setSelectedAssetForForm(null);
  };

  const handleCreate = (data: GatewayFormData) => {
    const formData = {
      ...data,
      customerId: selectedCustomerForForm?.id || undefined,
      assetId: selectedAssetForForm?.id || undefined,
    };
    createMutation.mutate(formData);
  };

  const handleEdit = (gateway: Gateway) => {
    setSelectedGateway(gateway);
    setValue('id', gateway.id);
    setValue('name', gateway.name || '');
    setValue('hardwareUuid', gateway.hardwareUuid);
    setValue('fetchIntervalMs', gateway.fetchIntervalMs || 300000);
    setValue('energyFetchIntervalMs', gateway.energyFetchIntervalMs || 300000);
    setValue('waterFetchIntervalMs', gateway.waterFetchIntervalMs || 300000);
    setValue('isPaused', gateway.isPaused || false);

    // Set selected entities for the form
    const selectedCustomer = customers.find(c => c.id === gateway.customerId);
    const selectedAsset = assets.find(a => a.id === gateway.assetId);
    setSelectedCustomerForForm(selectedCustomer || null);
    setSelectedAssetForForm(selectedAsset || null);

    setIsEditOpen(true);
  };

  const handleUpdate = (data: GatewayFormData) => {
    if (selectedGateway) {
      const formData = {
        ...data,
        customerId: selectedCustomerForForm?.id || undefined,
        assetId: selectedAssetForForm?.id || undefined,
      };
      updateMutation.mutate({ id: selectedGateway.id, data: formData });
    }
  };

  const handleDelete = (gateway: Gateway) => {
    if (window.confirm(`Are you sure you want to delete gateway "${gateway.name}"?`)) {
      deleteMutation.mutate(gateway.id);
    }
  };

  const handleRegister = (gateway: Gateway) => {
    if (window.confirm(`Are you sure you want to register gateway "${gateway.name || gateway.id}"?`)) {
      registerMutation.mutate(gateway.id);
    }
  };

  const handleUnregister = (gateway: Gateway) => {
    if (window.confirm(`Are you sure you want to unregister gateway "${gateway.name || gateway.id}"?`)) {
      unregisterMutation.mutate(gateway.id);
    }
  };

  const handlePopulateGatewayNames = (gateway: Gateway, forcePopulate = false) => {
    setGatewayToPopulate(gateway);
    setIsForcePopulateGateway(forcePopulate);
    setIsPopulateConfirmOpen(true);
  };

  const handleConfirmPopulate = () => {
    if (gatewayToPopulate) {
      populateGatewayNamesMutation.mutate({
        gatewayId: gatewayToPopulate.id,
        onlyMissingNames: !isForcePopulateGateway
      });
    }
  };

  const handleResetSync = (gateway: Gateway) => {
    setSelectedGateway(gateway);
    // Pre-fill with current timestamps if they exist, otherwise use a default from 24 hours ago
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const defaultTimestamp = yesterday.toISOString().slice(0, 16);

    resetSync({
      lastEnergyFetchTimestamp: gateway.lastEnergyFetchTimestamp
        ? new Date(gateway.lastEnergyFetchTimestamp).toISOString().slice(0, 16)
        : defaultTimestamp,
      lastWaterFetchTimestamp: gateway.lastWaterFetchTimestamp
        ? new Date(gateway.lastWaterFetchTimestamp).toISOString().slice(0, 16)
        : defaultTimestamp
    });
    setIsResetSyncOpen(true);
  };

  const handleConfirmResetSync = (data: ResetSyncFormData) => {
    if (selectedGateway) {
      resetSyncMutation.mutate({
        id: selectedGateway.id,
        data
      });
    }
  };

  // Reset page when search or filters change (but not on initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, selectedCustomerForFilter?.id, selectedAssetForFilter?.id, connectionFilter, statusFilter]);

  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCustomerForFilter(null);
    setSelectedAssetForFilter(null);
    setConnectionFilter('all');
    setStatusFilter('all');
    setSortBy('name');
    setSortOrder('asc');
  }, []);

  const gatewayColumns = [
    {
      key: 'gateway',
      header: t('gateway.gateway'),
      width: '280px',
      render: (gateway: Gateway) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 mr-3">
            <div className={`h-2 w-2 rounded-full ${gateway.isOnline ? 'bg-green-400' : 'bg-red-300'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-purple-600 transition-colors text-left flex items-center"
              onClick={() => handleEdit(gateway)}
            >
              {gateway.name || gateway.id}
              {!gateway.isRegistered && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                  Unregistered
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              <div className="flex items-center">
                <CpuChipIcon className="h-3 w-3 mr-1" />
                <span className="truncate">ID: {gateway.id}</span>
              </div>
              <div className="flex items-center">
                <CpuChipIcon className="h-3 w-3 mr-1 text-purple-500" />
                <span className="truncate">HW: {gateway.hardwareUuid}</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'assignment',
      header: 'Assignment',
      render: (gateway: Gateway) => (
        <div className="text-sm">
          {gateway.customer?.name ? (
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-3 w-3 text-blue-500 mr-1 flex-shrink-0" />
              <span className="text-gray-900 truncate">{gateway.customer.name}</span>
            </div>
          ) : gateway.asset?.label ? (
            <div className="flex items-center">
              <ServerStackIcon className="h-3 w-3 text-green-500 mr-1 flex-shrink-0" />
              <span className="text-gray-900 truncate">{gateway.asset.label}</span>
            </div>
          ) : (
            <span className="text-gray-400 text-xs">Unassigned</span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (gateway: Gateway) => (
        <div className="flex flex-col space-y-1">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${gateway.isOnline
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
            }`}>
            {gateway.isOnline ? 'Online' : 'Offline'}
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${gateway.isPaused
            ? 'bg-yellow-100 text-yellow-700'
            : 'bg-blue-100 text-blue-700'
            }`}>
            {gateway.isPaused ? 'Paused' : 'Running'}
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${gateway.isRegistered
            ? 'bg-green-100 text-green-700'
            : 'bg-orange-100 text-orange-700'
            }`}>
            {gateway.isRegistered ? 'Registered' : 'Unregistered'}
          </span>
        </div>
      )
    },
    {
      key: 'lastUpdated',
      header: t('common.lastUpdated'),
      width: '100px',
      render: (gateway: Gateway) => (
        <div className="text-xs text-gray-500">
          {new Date(gateway.updatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit'
          })}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      sticky: 'right' as const,
      width: '60px',
      render: (gateway: Gateway) => (
        <div className="flex items-center justify-end">
          <Menu as="div" className="relative z-50">
            <MenuButton className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <EllipsisVerticalIcon className="h-5 w-5" />
            </MenuButton>
            <MenuItems anchor="bottom end" className="w-48 bg-white rounded-md shadow-xl border border-gray-200 py-1 z-[9999] focus:outline-none">
              <MenuItem>
                <button
                  onClick={() => handlePopulateGatewayNames(gateway, false)}
                  disabled={populateGatewayNamesMutation.isPending}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center disabled:opacity-50 data-[focus]:bg-gray-100"
                >
                  <TagIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Populate Device Names
                  <span className="ml-auto text-xs text-gray-500">(only empty)</span>
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() => handlePopulateGatewayNames(gateway, true)}
                  disabled={populateGatewayNamesMutation.isPending}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center disabled:opacity-50 data-[focus]:bg-gray-100"
                >
                  <CpuChipIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Force Populate Names
                  <span className="ml-auto text-xs text-gray-500">(all devices)</span>
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() => handleResetSync(gateway)}
                  disabled={resetSyncMutation.isPending}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center disabled:opacity-50 data-[focus]:bg-gray-100"
                >
                  <ClockIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Reset Sync
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() => handleEdit(gateway)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center data-[focus]:bg-gray-100"
                >
                  <PencilIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Edit
                </button>
              </MenuItem>
              <hr className="my-1" />
              {gateway.isRegistered ? (
                <MenuItem>
                  <button
                    onClick={() => handleUnregister(gateway)}
                    disabled={unregisterMutation.isPending}
                    className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center disabled:opacity-50 data-[focus]:bg-orange-50"
                  >
                    <XMarkIcon className="h-4 w-4 mr-3 text-orange-400" />
                    Unregister
                  </button>
                </MenuItem>
              ) : (
                <MenuItem>
                  <button
                    onClick={() => handleRegister(gateway)}
                    disabled={registerMutation.isPending}
                    className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center disabled:opacity-50 data-[focus]:bg-green-50"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-3 text-green-400" />
                    Register
                  </button>
                </MenuItem>
              )}
              <MenuItem>
                <button
                  onClick={() => handleDelete(gateway)}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center data-[focus]:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4 mr-3 text-red-400" />
                  Delete
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('gateway.title')}</h1>
        <button
          onClick={() => {
            resetForm();
            setIsCreateOpen(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t('gateway.addGateway')}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search gateways by name, ID, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Customer:</label>
                <button
                  type="button"
                  onClick={() => setIsCustomerFilterSelectorOpen(true)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  {selectedCustomerForFilter ? (
                    <>
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 text-blue-600" />
                      {selectedCustomerForFilter.name}
                    </>
                  ) : (
                    <>
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400" />
                      All Customers
                    </>
                  )}
                </button>
                {selectedCustomerForFilter && (
                  <button
                    type="button"
                    onClick={() => setSelectedCustomerForFilter(null)}
                    className="inline-flex items-center p-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                    title="Clear customer filter"
                  >
                    <XCircleIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Asset:</label>
                <button
                  type="button"
                  onClick={() => setIsAssetFilterSelectorOpen(true)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  {selectedAssetForFilter ? (
                    <>
                      <ServerStackIcon className="h-4 w-4 mr-2 text-green-600" />
                      {selectedAssetForFilter.label}
                    </>
                  ) : (
                    <>
                      <ServerStackIcon className="h-4 w-4 mr-2 text-gray-400" />
                      All Assets
                    </>
                  )}
                </button>
                {selectedAssetForFilter && (
                  <button
                    type="button"
                    onClick={() => setSelectedAssetForFilter(null)}
                    className="inline-flex items-center p-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                    title="Clear asset filter"
                  >
                    <XCircleIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Connection:</label>
                <select
                  value={connectionFilter}
                  onChange={(e) => setConnectionFilter(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm px-3 py-2"
                >
                  <option value="all">All Status</option>
                  <option value="online">Online Only</option>
                  <option value="offline">Offline Only</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm px-3 py-2"
                >
                  <option value="all">All Gateways</option>
                  <option value="running">Running</option>
                  <option value="paused">Paused</option>
                  <option value="registered">Registered</option>
                  <option value="unregistered">Unregistered</option>
                  <option value="syncing">Syncing</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Sort:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm px-3 py-2"
                >
                  <option value="name">Name</option>
                  <option value="createdAt">Created Date</option>
                  <option value="updatedAt">Updated Date</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm px-3 py-2"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>

              {(searchTerm || selectedCustomerForFilter || selectedAssetForFilter || connectionFilter !== 'all' || statusFilter !== 'all' || sortBy !== 'name' || sortOrder !== 'asc') && (
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-orange-600 hover:text-orange-800 font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Results Summary */}
            <div className="flex justify-between items-center text-sm text-gray-600">
              <div>
                {t('gateway.showingCount', { count: gatewaysData?.data?.length || 0, total: gatewaysData?.pagination?.total || 0 })}
                {debouncedSearchTerm && (
                  <span className="ml-2">{t('gateway.searchingFor', { term: debouncedSearchTerm })}</span>
                )}
              </div>
              {isLoading && (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                  <span>{t('gateway.searching')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gateway List */}
      <DataTable
        data={gatewaysData?.data || []}
        columns={gatewayColumns}
        isLoading={isLoading}
        error={error}
        pagination={gatewaysData?.pagination}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        emptyStateIcon={WifiIcon}
        emptyStateTitle={t('gateway.noGateways')}
        emptyStateDescription={t('gateway.noGatewaysDescription')}
      />

      {/* Create Gateway Dialog */}
      <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-6xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Create New Gateway
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                Configure a new gateway for data collection from your devices
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(handleCreate)} className="space-y-8">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Basic Information
                    </h3>

                    <div>
                      <label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-2">
                        Gateway ID *
                      </label>
                      <input
                        type="text"
                        id="id"
                        {...register('id')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="Enter gateway ID"
                      />
                      {errors.id && (
                        <p className="mt-2 text-sm text-red-600">{errors.id.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Gateway Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        {...register('name')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="Enter gateway name"
                      />
                      {errors.name && (
                        <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="hardwareUuid" className="block text-sm font-medium text-gray-700 mb-2">
                        Hardware UUID *
                      </label>
                      <input
                        type="text"
                        id="hardwareUuid"
                        {...register('hardwareUuid')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="Enter hardware UUID (e.g., actual gateway UUID for API calls)"
                        onBlur={(e) => {
                          // Auto-fill with gateway ID if empty and gateway ID is filled
                          if (!e.target.value) {
                            const gatewayId = (document.getElementById('id') as HTMLInputElement)?.value;
                            if (gatewayId) {
                              setValue('hardwareUuid', gatewayId);
                            }
                          }
                        }}
                      />
                      {errors.hardwareUuid && (
                        <p className="mt-2 text-sm text-red-600">{errors.hardwareUuid.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        The actual UUID of the physical gateway hardware for API communication
                      </p>
                    </div>

                    <div>
                      <label htmlFor="fetchIntervalMs" className="block text-sm font-medium text-gray-700 mb-2">
                        Legacy Fetch Interval (milliseconds)
                      </label>
                      <input
                        type="number"
                        id="fetchIntervalMs"
                        min="1000"
                        defaultValue={300000}
                        {...register('fetchIntervalMs', { valueAsNumber: true })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="300000"
                      />
                      {errors.fetchIntervalMs && (
                        <p className="mt-2 text-sm text-red-600">{errors.fetchIntervalMs.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        Legacy combined interval for backward compatibility
                      </p>
                    </div>

                    <div>
                      <label htmlFor="energyFetchIntervalMs" className="block text-sm font-medium text-gray-700 mb-2">
                        Energy Fetch Interval (milliseconds)
                      </label>
                      <input
                        type="number"
                        id="energyFetchIntervalMs"
                        min="1000"
                        defaultValue={300000}
                        {...register('energyFetchIntervalMs', { valueAsNumber: true })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="300000"
                      />
                      {errors.energyFetchIntervalMs && (
                        <p className="mt-2 text-sm text-red-600">{errors.energyFetchIntervalMs.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        How often to fetch energy data from this gateway
                      </p>
                    </div>

                    <div>
                      <label htmlFor="waterFetchIntervalMs" className="block text-sm font-medium text-gray-700 mb-2">
                        Water Fetch Interval (milliseconds)
                      </label>
                      <input
                        type="number"
                        id="waterFetchIntervalMs"
                        min="1000"
                        defaultValue={300000}
                        {...register('waterFetchIntervalMs', { valueAsNumber: true })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="300000"
                      />
                      {errors.waterFetchIntervalMs && (
                        <p className="mt-2 text-sm text-red-600">{errors.waterFetchIntervalMs.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        How often to fetch water data from this gateway
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isPaused"
                          {...register('isPaused')}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isPaused" className="ml-3 block text-sm font-medium text-gray-700">
                          Pause Gateway
                        </label>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        When paused, the gateway will not collect data from devices
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Assignment
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsCustomerSelectorOpen(true)}
                          className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-white"
                        >
                          {selectedCustomerForForm ? (
                            <div className="flex items-center space-x-3">
                              <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                              <div>
                                <span className="font-medium text-gray-900">{selectedCustomerForForm.name}</span>
                                <span className="block text-sm text-gray-500">{selectedCustomerForForm.description || 'No description'}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-3 text-gray-500">
                              <BuildingOfficeIcon className="h-5 w-5" />
                              <span>Select a customer</span>
                            </div>
                          )}
                        </button>
                        {errors.customerId && (
                          <p className="mt-2 text-sm text-red-600">{errors.customerId.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Asset
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsAssetSelectorOpen(true)}
                          className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-white"
                        >
                          {selectedAssetForForm ? (
                            <div className="flex items-center space-x-3">
                              <ServerStackIcon className="h-5 w-5 text-green-600" />
                              <div>
                                <span className="font-medium text-gray-900">{selectedAssetForForm.label}</span>
                                <span className="block text-sm text-gray-500">{selectedAssetForForm.additional_info || 'No description'}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-3 text-gray-500">
                              <ServerStackIcon className="h-5 w-5" />
                              <span>Select an asset</span>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsCreateOpen(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-6 py-3 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {createMutation.isPending ? 'Creating Gateway...' : 'Create Gateway'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Edit Gateway Dialog */}
      <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-6xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Edit Gateway
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                Update gateway configuration and assignment
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(handleUpdate)} className="space-y-8">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Basic Information
                    </h3>

                    <div>
                      <label htmlFor="edit-id" className="block text-sm font-medium text-gray-700 mb-2">
                        Gateway ID *
                      </label>
                      <input
                        type="text"
                        id="edit-id"
                        {...register('id')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-gray-100"
                        placeholder="Gateway ID"
                        disabled
                      />
                      {errors.id && (
                        <p className="mt-2 text-sm text-red-600">{errors.id.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Gateway Name
                      </label>
                      <input
                        type="text"
                        id="edit-name"
                        {...register('name')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="Enter gateway name"
                      />
                      {errors.name && (
                        <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-hardwareUuid" className="block text-sm font-medium text-gray-700 mb-2">
                        Hardware UUID *
                      </label>
                      <input
                        type="text"
                        id="edit-hardwareUuid"
                        {...register('hardwareUuid')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="Enter hardware UUID (e.g., actual gateway UUID for API calls)"
                      />
                      {errors.hardwareUuid && (
                        <p className="mt-2 text-sm text-red-600">{errors.hardwareUuid.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        The actual UUID of the physical gateway hardware for API communication
                      </p>
                    </div>

                    <div>
                      <label htmlFor="edit-fetchIntervalMs" className="block text-sm font-medium text-gray-700 mb-2">
                        Legacy Fetch Interval (milliseconds)
                      </label>
                      <input
                        type="number"
                        id="edit-fetchIntervalMs"
                        min="1000"
                        {...register('fetchIntervalMs', { valueAsNumber: true })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="300000"
                      />
                      {errors.fetchIntervalMs && (
                        <p className="mt-2 text-sm text-red-600">{errors.fetchIntervalMs.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        Legacy combined interval for backward compatibility
                      </p>
                    </div>

                    <div>
                      <label htmlFor="edit-energyFetchIntervalMs" className="block text-sm font-medium text-gray-700 mb-2">
                        Energy Fetch Interval (milliseconds)
                      </label>
                      <input
                        type="number"
                        id="edit-energyFetchIntervalMs"
                        min="1000"
                        {...register('energyFetchIntervalMs', { valueAsNumber: true })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="300000"
                      />
                      {errors.energyFetchIntervalMs && (
                        <p className="mt-2 text-sm text-red-600">{errors.energyFetchIntervalMs.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        How often to fetch energy data from this gateway
                      </p>
                    </div>

                    <div>
                      <label htmlFor="edit-waterFetchIntervalMs" className="block text-sm font-medium text-gray-700 mb-2">
                        Water Fetch Interval (milliseconds)
                      </label>
                      <input
                        type="number"
                        id="edit-waterFetchIntervalMs"
                        min="1000"
                        {...register('waterFetchIntervalMs', { valueAsNumber: true })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="300000"
                      />
                      {errors.waterFetchIntervalMs && (
                        <p className="mt-2 text-sm text-red-600">{errors.waterFetchIntervalMs.message}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        How often to fetch water data from this gateway
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="edit-isPaused"
                          {...register('isPaused')}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <label htmlFor="edit-isPaused" className="ml-3 block text-sm font-medium text-gray-700">
                          Pause Gateway
                        </label>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        When paused, the gateway will not collect data from devices
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Assignment
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsCustomerSelectorOpen(true)}
                          className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-white"
                        >
                          {selectedCustomerForForm ? (
                            <div className="flex items-center space-x-3">
                              <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                              <div>
                                <span className="font-medium text-gray-900">{selectedCustomerForForm.name}</span>
                                <span className="block text-sm text-gray-500">{selectedCustomerForForm.description || 'No description'}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-3 text-gray-500">
                              <BuildingOfficeIcon className="h-5 w-5" />
                              <span>Select a customer</span>
                            </div>
                          )}
                        </button>
                        {errors.customerId && (
                          <p className="mt-2 text-sm text-red-600">{errors.customerId.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Asset
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsAssetSelectorOpen(true)}
                          className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-white"
                        >
                          {selectedAssetForForm ? (
                            <div className="flex items-center space-x-3">
                              <ServerStackIcon className="h-5 w-5 text-green-600" />
                              <div>
                                <span className="font-medium text-gray-900">{selectedAssetForForm.label}</span>
                                <span className="block text-sm text-gray-500">{selectedAssetForForm.additional_info || 'No description'}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-3 text-gray-500">
                              <ServerStackIcon className="h-5 w-5" />
                              <span>Select an asset</span>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Fetch Timestamps Information */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2 text-gray-600" />
                    Data Fetch Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-gray-700">
                        Last Energy Fetch
                      </label>
                      <div className="text-sm text-gray-900">
                        {selectedGateway?.lastEnergyFetchTimestamp ? (
                          <div className="space-y-2">
                            <span className="font-mono bg-white px-2 py-1 rounded border">
                              {new Date(selectedGateway.lastEnergyFetchTimestamp).toLocaleString()}
                            </span>
                            <div className="text-xs text-gray-500">
                              {new Date(selectedGateway.lastEnergyFetchTimestamp).toISOString()}
                            </div>
                            {/* Progress Bar */}
                            <div className="w-full">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-orange-700">Progress</span>
                                <span className="text-xs font-medium text-orange-700">
                                  {calculateFetchProgress(selectedGateway.lastEnergyFetchTimestamp).toFixed(1)}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${calculateFetchProgress(selectedGateway.lastEnergyFetchTimestamp)}%` }}
                                ></div>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>Jan 2025</span>
                                <span>Now</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <span className="text-gray-400 italic">Never fetched</span>
                            {/* Progress Bar for never fetched */}
                            <div className="w-full">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-gray-500">Progress</span>
                                <span className="text-xs font-medium text-gray-500">0%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-gray-300 h-2 rounded-full w-0"></div>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>Jan 2025</span>
                                <span>Now</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="text-sm font-medium text-gray-700">
                        Last Water Fetch
                      </label>
                      <div className="text-sm text-gray-900">
                        {selectedGateway?.lastWaterFetchTimestamp ? (
                          <div className="space-y-2">
                            <span className="font-mono bg-white px-2 py-1 rounded border">
                              {new Date(selectedGateway.lastWaterFetchTimestamp).toLocaleString()}
                            </span>
                            <div className="text-xs text-gray-500">
                              {new Date(selectedGateway.lastWaterFetchTimestamp).toISOString()}
                            </div>
                            {/* Progress Bar */}
                            <div className="w-full">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-blue-700">Progress</span>
                                <span className="text-xs font-medium text-blue-700">
                                  {calculateFetchProgress(selectedGateway.lastWaterFetchTimestamp).toFixed(1)}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${calculateFetchProgress(selectedGateway.lastWaterFetchTimestamp)}%` }}
                                ></div>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>Jan 2025</span>
                                <span>Now</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <span className="text-gray-400 italic">Never fetched</span>
                            {/* Progress Bar for never fetched */}
                            <div className="w-full">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-gray-500">Progress</span>
                                <span className="text-xs font-medium text-gray-500">0%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-gray-300 h-2 rounded-full w-0"></div>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>Jan 2025</span>
                                <span>Now</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-gray-600">
                    <p>These timestamps indicate the last time energy and water data were successfully fetched from this gateway.</p>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditOpen(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-6 py-3 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {updateMutation.isPending ? 'Updating Gateway...' : 'Update Gateway'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Advanced Entity Selectors */}
      <AdvancedEntitySelector
        isOpen={isCustomerSelectorOpen}
        onClose={() => setIsCustomerSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedCustomerForForm(entity as Customer);
          setSelectedAssetForForm(null); // Clear asset when customer changes
        }}
        entityType="customer"
        title="Select Customer"
        selectedId={selectedCustomerForForm?.id}
      />

      <AdvancedEntitySelector
        isOpen={isAssetSelectorOpen}
        onClose={() => setIsAssetSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedAssetForForm(entity as Asset);
          setSelectedCustomerForForm(null); // Clear customer when asset changes
        }}
        entityType="asset"
        title="Select Asset"
        selectedId={selectedAssetForForm?.id}
        customerFilter={selectedCustomerForForm?.id}
      />

      <AdvancedEntitySelector
        isOpen={isCustomerFilterSelectorOpen}
        onClose={() => setIsCustomerFilterSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedCustomerForFilter(entity as Customer);
        }}
        entityType="customer"
        title="Filter by Customer"
        selectedId={selectedCustomerForFilter?.id}
      />

      <AdvancedEntitySelector
        isOpen={isAssetFilterSelectorOpen}
        onClose={() => setIsAssetFilterSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedAssetForFilter(entity as Asset);
        }}
        entityType="asset"
        title="Filter by Asset"
        selectedId={selectedAssetForFilter?.id}
        customerFilter={selectedCustomerForFilter?.id}
      />

      {/* Populate Gateway Names Confirmation Dialog */}
      <Dialog open={isPopulateConfirmOpen} onClose={() => setIsPopulateConfirmOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <DialogTitle className="text-lg font-medium text-gray-900">
                {isForcePopulateGateway ? 'Force Populate Device Names' : 'Populate Device Names'}
              </DialogTitle>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                {isForcePopulateGateway 
                  ? <>This will update names for ALL devices in gateway{' '}
                    <span className="font-medium">{gatewayToPopulate?.name || gatewayToPopulate?.id}</span>{' '}
                    using the latest data from the gateway API. Existing names will be overwritten.</>
                  : <>This will populate device names for all devices in gateway{' '}
                    <span className="font-medium">{gatewayToPopulate?.name || gatewayToPopulate?.id}</span>{' '}
                    that currently have missing names, using data from the gateway API.</>
                }
              </p>
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-800">
                  {isForcePopulateGateway 
                    ? <>• ALL devices will be updated (existing names overwritten)<br />
                      • Water devices will use flow sensor channel names<br />
                      • Energy devices will use slave names<br />
                      • Failed API calls will be safely ignored</>
                    : <>• Only devices with missing names will be updated<br />
                      • Water devices will use flow sensor channel names<br />
                      • Energy devices will use slave names<br />
                      • Failed API calls will be safely ignored</>
                  }
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsPopulateConfirmOpen(false);
                  setGatewayToPopulate(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={populateGatewayNamesMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmPopulate}
                disabled={populateGatewayNamesMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {populateGatewayNamesMutation.isPending 
                  ? 'Populating...' 
                  : isForcePopulateGateway ? 'Force Update Names' : 'Populate Names'
                }
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Reset Sync Timestamps Dialog */}
      <Dialog open={isResetSyncOpen} onClose={() => setIsResetSyncOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <DialogTitle className="text-lg font-medium text-gray-900">
                Reset Gateway Sync Timestamps
              </DialogTitle>
            </div>
            <form onSubmit={handleSubmitSync(handleConfirmResetSync)}>
              <div className="px-6 py-4 space-y-4">
                <div className="bg-yellow-50 p-4 rounded-md">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        What this does:
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Resets the last fetch timestamps for this gateway</li>
                          <li>MQTT service will automatically re-fetch data from the specified dates</li>
                          <li>Use this to backfill missing data after system issues</li>
                          <li>Leave fields empty to keep current timestamps unchanged</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Gateway: <span className="font-medium">{selectedGateway?.name || selectedGateway?.id}</span>
                  </p>
                </div>

                <div>
                  <label htmlFor="reset-energy-timestamp" className="block text-sm font-medium text-gray-700 mb-2">
                    Energy Fetch Timestamp
                  </label>
                  <input
                    type="datetime-local"
                    id="reset-energy-timestamp"
                    {...registerSync('lastEnergyFetchTimestamp')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  />
                  {syncErrors.lastEnergyFetchTimestamp && (
                    <p className="mt-1 text-sm text-red-600">
                      {syncErrors.lastEnergyFetchTimestamp.message}
                    </p>
                  )}
                  {selectedGateway?.lastEnergyFetchTimestamp && (
                    <p className="mt-1 text-xs text-gray-500">
                      Current: {new Date(selectedGateway.lastEnergyFetchTimestamp).toLocaleString()}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="reset-water-timestamp" className="block text-sm font-medium text-gray-700 mb-2">
                    Water Fetch Timestamp
                  </label>
                  <input
                    type="datetime-local"
                    id="reset-water-timestamp"
                    {...registerSync('lastWaterFetchTimestamp')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  />
                  {syncErrors.lastWaterFetchTimestamp && (
                    <p className="mt-1 text-sm text-red-600">
                      {syncErrors.lastWaterFetchTimestamp.message}
                    </p>
                  )}
                  {selectedGateway?.lastWaterFetchTimestamp && (
                    <p className="mt-1 text-xs text-gray-500">
                      Current: {new Date(selectedGateway.lastWaterFetchTimestamp).toLocaleString()}
                    </p>
                  )}
                </div>

                {Object.keys(syncErrors).length > 0 && (
                  <div className="text-sm text-red-600">
                    {syncErrors.root?.message}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsResetSyncOpen(false);
                    setSelectedGateway(null);
                    resetSync();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={resetSyncMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetSyncMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resetSyncMutation.isPending ? 'Resetting...' : 'Reset Timestamps'}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </Dialog>

    </div>
  );
}
